import { useLazyQuery } from '@apollo/client';
import { IntegrationStates, NavigationStyles } from '@generated/graphql';
import { type DebouncedFunc, debounce } from 'lodash-es';
import type React from 'react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CONFIG } from '../config';
import { useLocalization } from '../hooks/useLocalization';
import { usePermission } from '../hooks/usePermission';
import { FETCH_INTEGRATIONS } from '../pages/DevSettings/integrationsTab.query';
import {
  initAnalytics,
  sendAnalyticsEvent,
} from '../utils/analytics/analytics';
import {
  type AnalyticsProviderType,
  AnalyticsProviderTypes,
} from '../utils/analytics/providerTypes';
import type { AnalyticsMeta, AnalyticsSendFn } from '../utils/analytics/types';
import { UserContext } from './userContextProvider';

export type { AnalyticsProviderType } from '../utils/analytics/providerTypes';
export { AnalyticsProviderTypes } from '../utils/analytics/providerTypes';

type AnalyticsContextType = {
  sendAnalytic: AnalyticsSendFn;
};

const ANALYTICS_DEDUPE_MS = 100;

type DebouncedAnalyticsSend = DebouncedFunc<
  (
    providers: AnalyticsProviderType[],
    event: string,
    meta: AnalyticsMeta,
  ) => void
>;

const noopSendAnalytic: AnalyticsSendFn = () => undefined;

const firebaseDefaultConfig = {
  apiKey: CONFIG.analytics.apiKey ?? '',
  authDomain: CONFIG.analytics.authDomain ?? '',
  projectId: CONFIG.analytics.projectId ?? '',
  storageBucket: CONFIG.analytics.storageBucket ?? '',
  messagingSenderId: CONFIG.analytics.messagingSenderId ?? '',
  appId: CONFIG.analytics.appId ?? '',
  measurementId: CONFIG.analytics.measurementId ?? '',
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  sendAnalytic: noopSendAnalytic,
});
const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization, userContext, activeEntity } =
    useContext(UserContext);
  const [provider, setProvider] = useState<AnalyticsProviderType[]>([]);
  const debouncedSendByEventRef = useRef(
    new Map<string, DebouncedAnalyticsSend>(),
  );
  const canReadIntegrations = usePermission('read:integrations');
  const getDefaultFirebase = () => {
    try {
      initAnalytics({
        provider: AnalyticsProviderTypes.GOOGLE,
        providerId: 'onevest-default',
        configType: firebaseDefaultConfig,
      });
    } catch {
      /* empty */
    }
  };
  const getDefaultMixPanel = () => {
    try {
      initAnalytics({
        provider: AnalyticsProviderTypes.MIXPANEL,
        providerId: 'onevest-default',
        configType: {
          projectToken: CONFIG.analytics.mixPanelProjectToken ?? '',
        },
        user: {
          id: activeEntity?.id ?? userContext.id,
          organizationName: translateBackendString(
            userContext.organization?.translatedName,
          ),
          organizationId: activeOrganization.id,
          userRole: translateBackendString(userContext.role?.translatedName),
        },
      });
    } catch {
      /* empty */
    }
  };
  const sendAnalytic = useCallback<AnalyticsSendFn>(
    (event, meta) => {
      const mergedMeta: AnalyticsMeta = {
        ...(meta ?? {}),
        subdomainName: userContext.organization?.subdomain,
        organizationName: translateBackendString(
          userContext.organization?.translatedName,
        ),
        organizationId: activeOrganization.id,
        userRole: translateBackendString(userContext.role?.translatedName),
        orgName: userContext.organization?.translatedName?.en,
        orgId: activeOrganization.id,
        role: userContext.role?.translatedName?.en,
        roleProfile: userContext.role?.publicRoleProfile?.translatedName?.en,
      };
      let debouncedSend = debouncedSendByEventRef.current.get(event);
      if (!debouncedSend) {
        debouncedSend = debounce(sendAnalyticsEvent, ANALYTICS_DEDUPE_MS, {
          leading: true,
          trailing: false,
        });
        debouncedSendByEventRef.current.set(event, debouncedSend);
      }
      debouncedSend(provider, event, mergedMeta);
    },
    [
      activeOrganization.id,
      provider,
      userContext.organization?.translatedName?.en,
      userContext.organization?.subdomain,
      userContext.role?.translatedName?.en,
      userContext.role?.publicRoleProfile?.translatedName?.en,
      userContext.organization?.translatedName,
      userContext.role?.translatedName,
    ],
  );

  useEffect(() => {
    const debouncedSends = debouncedSendByEventRef.current;
    return () => {
      debouncedSends.forEach((debouncedSend) => {
        debouncedSend.cancel();
      });
      debouncedSends.clear();
    };
  }, []);

  const contextValue = useMemo(() => ({ sendAnalytic }), [sendAnalytic]);
  const [fetchIntegration] = useLazyQuery(FETCH_INTEGRATIONS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          type: 'ANALYTICS',
          state: IntegrationStates.Active,
        },
      },
    },
    onCompleted: (response): void => {
      const mixpanelToken = new Set<string>();
      const fetchedIntegrations =
        response.fetchIntegrations.integrations.filter((integration: any) => {
          if (integration.provider === AnalyticsProviderTypes.MIXPANEL) {
            if (!mixpanelToken.has(integration.configuration.TBD)) {
              mixpanelToken.add(integration.configuration.TBD);
              return true;
            }
            return false;
          }
          return true;
        });

      // Check if there's a custom Mixpanel integration to avoid duplicate events
      const hasCustomMixpanel = fetchedIntegrations.some(
        (i: any) => i.provider === AnalyticsProviderTypes.MIXPANEL,
      );

      // Build providers list, avoiding duplicate Mixpanel providers
      const analyticsIntegrations: AnalyticsProviderType[] = [
        { type: AnalyticsProviderTypes.GOOGLE, id: 'onevest-default' },
      ];

      // Only add default Mixpanel if no custom Mixpanel integration exists
      if (!hasCustomMixpanel) {
        analyticsIntegrations.push({
          type: AnalyticsProviderTypes.MIXPANEL,
          id: 'onevest-default',
        });
      }

      // Add all custom integrations from backend
      fetchedIntegrations.forEach((integration: any) => {
        analyticsIntegrations.push({
          type: integration.provider,
          id: integration.id,
        });
      });

      setProvider(analyticsIntegrations);

      getDefaultFirebase();
      if (!hasCustomMixpanel) {
        getDefaultMixPanel();
      }

      // Initialize custom integrations
      fetchedIntegrations.forEach((integration: any): void => {
        if (integration.provider === AnalyticsProviderTypes.GOOGLE) {
          try {
            initAnalytics({
              provider: AnalyticsProviderTypes.GOOGLE,
              providerId: integration.id,
              configType: {
                apiKey: integration.configuration.apiKey,
                authDomain: integration.configuration.authDomain,
                projectId: integration.configuration.projectId,
                storageBucket: integration.configuration.storageBucket,
                messagingSenderId: integration.configuration.messagingSenderId,
                appId: integration.configuration.appId,
                measurementId: integration.configuration.measurementId,
              },
            });
          } catch {
            /* empty */
          }
        }
        if (integration.provider === AnalyticsProviderTypes.MIXPANEL) {
          initAnalytics({
            provider: AnalyticsProviderTypes.MIXPANEL,
            providerId: integration.id,
            configType: {
              projectToken: integration.configuration.TBD,
            },
            user: {
              id: activeEntity?.id ?? userContext.id,
              organizationName: translateBackendString(
                userContext.organization?.translatedName,
              ),
              organizationId: activeOrganization.id,
              userRole: translateBackendString(
                userContext.role?.translatedName,
              ),
            },
          });
        }
      });
    },
    onError: (): void => {
      setDefaultProviders();
    },
  });

  const setDefaultProviders = () => {
    getDefaultFirebase();
    getDefaultMixPanel();
    setProvider([
      { type: AnalyticsProviderTypes.GOOGLE, id: 'onevest-default' },
      { type: AnalyticsProviderTypes.MIXPANEL, id: 'onevest-default' },
    ]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: rules conflicts with dependency array
  useEffect(() => {
    const hasUserContext =
      userContext && Object.entries(userContext).length > 0;
    const isUnifiedClientExperience =
      userContext.role?.navigationStyle === NavigationStyles.Simple;
    if (
      (hasUserContext && isUnifiedClientExperience && activeEntity) ||
      (!isUnifiedClientExperience && hasUserContext)
    ) {
      if (canReadIntegrations) {
        if (!activeOrganization.id) return;
        fetchIntegration().then();
      } else {
        setDefaultProviders();
      }
    }
  }, [
    fetchIntegration,
    activeEntity,
    userContext,
    canReadIntegrations,
    activeOrganization.id,
  ]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export { AnalyticsContext, AnalyticsProvider };
